import React, { useState, useCallback, useRef, useEffect } from "react";
import { Card, Button, Table, Space, Badge, Popconfirm, Tour, Tag, Popover, Progress } from "antd";
import { PlusOutlined, SyncOutlined, QuestionCircleOutlined, ImportOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTableSelection, useDelete } from "../../../hooks";
import { mawbStockService } from "../../../api/enhancedApi";
import { useInfiniteList, useTableBodyHeight } from "../hooks/useInfiniteList";
import { formatDateTime } from "../../../utils/dateUtils";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import MawbStockFilter from "./components/MawbStockFilter";
import MawbStockCreateModal from "./components/MawbStockCreateModal";
import MawbAllocationModal from "./components/MawbAllocationModal";
import MawbImportModal from "./components/MawbImportModal";

const statusMap = {
    AVAILABLE: { text: "可用", status: "success" },
    PARTIALLY_ALLOCATED: { text: "部分分配", status: "warning" },
    FULLY_ALLOCATED: { text: "全部分配", status: "default" },
};

const STOCK_PAGE_SIZE = 50;
const STOCK_TABLE_OFFSET = 300;

const sourceMap = {
    RANGE: { text: "号段", color: "blue" },
    IMPORT: { text: "导入", color: "purple" },
};

const MawbStockListPage = () => {
    const navigate = useNavigate();
    const [filterParams, setFilterParams] = useState({});
    const tableWrapRef = useRef(null);
    const tableScrollY = useTableBodyHeight(STOCK_TABLE_OFFSET);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [allocModalOpen, setAllocModalOpen] = useState(false);
    const [allocStock, setAllocStock] = useState(null);
    const [tourOpen, setTourOpen] = useState(false);
    const [createAutoGuide, setCreateAutoGuide] = useState(false);

    // 页面内新手引导（antd Tour）锚点
    const createBtnRef = useRef(null);
    const filterRef = useRef(null);
    const tableCardRef = useRef(null);

    const {
        items,
        total,
        loading: isLoading,
        loadingMore,
        stale,
        hasMore,
        reload: refetch,
        loadMore,
    } = useInfiniteList({
        fetcher: mawbStockService.getList,
        query: filterParams,
        pageSize: STOCK_PAGE_SIZE,
        scrollBodyRef: tableWrapRef,
    });

    // 已加载号段的分配对象统计（批量拉取，避免逐行 N+1）。
    // key 含 available_quantity，使分配/收回后能随列表刷新而重新统计。
    const [summaryMap, setSummaryMap] = useState({});
    const summaryKey = items.map((it) => `${it.id}:${it.available_quantity}`).join(",");

    useEffect(() => {
        if (!summaryKey) {
            setSummaryMap({});
            return undefined;
        }
        const ids = summaryKey.split(",").map((pair) => parseInt(pair.split(":")[0], 10));
        let cancelled = false;
        mawbStockService
            .getAllocationSummaryBulk(ids)
            .then((res) => {
                if (!cancelled) setSummaryMap(res.data?.summaries || {});
            })
            .catch(() => {
                if (!cancelled) setSummaryMap({});
            });
        return () => {
            cancelled = true;
        };
    }, [summaryKey]);

    const { tableRef, getIndexColumn } = useTableSelection({
        rowKey: "id",
        dataSource: items,
        pagination: { current: 1, pageSize: 1 },
    });

    const handleSearch = useCallback((params) => {
        setFilterParams(params);
    }, []);

    const handleReset = useCallback(() => {
        setFilterParams({});
    }, []);

    const handleAllocate = (record) => {
        setAllocStock(record);
        setAllocModalOpen(true);
    };

    const deleteMutation = useDelete("号段", mawbStockService.remove, {
        onSuccess: () => refetch(),
    });

    const handleDelete = (id) => {
        deleteMutation.mutate(id);
    };

    const renderStatus = (status) => {
        const cfg = statusMap[status] || { text: status, status: "default" };
        return <Badge status={cfg.status} text={cfg.text} />;
    };

    const objTagColor = (type) => (type === "internal" ? "gold" : "blue");
    const objTypeLabel = (type) => (type === "internal" ? "内部" : "合作伙伴");

    const renderAllocation = (_, record) => {
        const total = record.quantity || 0;
        const allocated = total - (record.available_quantity || 0);
        const percent = total > 0 ? Math.round((allocated / total) * 100) : 0;
        const items = summaryMap[String(record.id)] || [];
        const top = items.slice(0, 2);
        const rest = items.length - top.length;

        const bar = (
            <div style={{ minWidth: 170, cursor: items.length ? "pointer" : "default" }}>
                <Progress
                    percent={percent}
                    size="small"
                    status={percent === 100 ? "success" : "active"}
                    format={() => `${allocated}/${total}`}
                />
                <div style={{ marginTop: 2, minHeight: 22 }}>
                    {items.length === 0 ? (
                        <span style={{ color: "#bbb", fontSize: 12 }}>未分配</span>
                    ) : (
                        <Space size={4} wrap>
                            {top.map((s) => (
                                <Tag
                                    key={`${s.allocated_to_type}-${s.allocated_to_name}`}
                                    color={objTagColor(s.allocated_to_type)}
                                    style={{ marginInlineEnd: 0 }}
                                >
                                    {s.allocated_to_name} {s.count}
                                </Tag>
                            ))}
                            {rest > 0 && <Tag style={{ marginInlineEnd: 0 }}>+{rest}</Tag>}
                        </Space>
                    )}
                </div>
            </div>
        );

        if (items.length === 0) return bar;

        const popoverContent = (
            <div style={{ maxWidth: 280 }}>
                {items.map((s) => (
                    <div
                        key={`${s.allocated_to_type}-${s.allocated_to_name}`}
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 16,
                            padding: "3px 0",
                        }}
                    >
                        <Space size={4}>
                            <Tag color={objTagColor(s.allocated_to_type)} style={{ marginInlineEnd: 0 }}>
                                {objTypeLabel(s.allocated_to_type)}
                            </Tag>
                            <span>{s.allocated_to_name}</span>
                        </Space>
                        <strong>
                            {s.count}
                            <span style={{ color: "#999", fontWeight: 400 }}>
                                {total > 0 ? ` (${Math.round((s.count / total) * 100)}%)` : ""}
                            </span>
                        </strong>
                    </div>
                ))}
            </div>
        );

        return (
            <Popover content={popoverContent} title="分配对象明细" placement="left" trigger="hover">
                {bar}
            </Popover>
        );
    };

    const columns = [
        getIndexColumn("序号", 60),
        { title: "航司前缀", dataIndex: "airline_prefix", key: "airline_prefix", width: 100, align: "center" },
        {
            title: "来源", dataIndex: "source", key: "source", width: 80, align: "center",
            render: (src) => {
                const cfg = sourceMap[src] || sourceMap.RANGE;
                return <Tag color={cfg.color}>{cfg.text}</Tag>;
            },
        },
        { title: "起始号", dataIndex: "start_serial", key: "start_serial", width: 120, align: "center" },
        { title: "结束号", dataIndex: "end_serial", key: "end_serial", width: 120, align: "center" },
        { title: "总数", dataIndex: "quantity", key: "quantity", width: 80, align: "center" },
        { title: "可用", dataIndex: "available_quantity", key: "available_quantity", width: 80, align: "center" },
        {
            title: "状态", dataIndex: "status", key: "status", width: 120, align: "center",
            render: renderStatus,
        },
        {
            title: "分配情况", key: "allocation", width: 220, render: renderAllocation,
        },
        {
            title: "收到日期", dataIndex: "received_date", key: "received_date", width: 120, align: "center",
        },
        {
            title: "创建时间", dataIndex: "created_at", key: "created_at", width: 160, align: "center",
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作", key: "action", fixed: "right", width: 220, align: "center",
            render: (_, record) => (
                <Space size="small">
                    <Button type="link" size="small" onClick={() => navigate(`/mawb/stock/${record.id}`)}>
                        详情
                    </Button>
                    <PermissionGuard permission="mawb:allocate">
                        <Button
                            type="link"
                            size="small"
                            disabled={record.status === "FULLY_ALLOCATED"}
                            onClick={() => handleAllocate(record)}
                        >
                            分配
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="mawb:delete">
                        <Popconfirm
                            title="确认删除该号段？"
                            description="仅当号段内无已分配/已使用的主单号时可删除"
                            okText="删除"
                            cancelText="取消"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <Button type="link" size="small" danger>
                                删除
                            </Button>
                        </Popconfirm>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    const tourSteps = [
        {
            title: "筛选查找",
            description: "按航司前缀、状态、收到日期等条件，快速定位已入库的号段。",
            target: () => filterRef.current,
        },
        {
            title: "号段与状态",
            description:
                "每一行是一个号段：可查看总数、可用数量，以及「可用 / 部分分配 / 全部分配」状态。",
            target: () => tableCardRef.current,
        },
        {
            title: "详情 · 分配 · 收回 · 删除",
            description:
                "操作列可进入「详情」查看每个主单号并分配 / 收回；「分配」把整批分给客户或货代；「删除」可移除整批（仅在无已分配 / 已使用时）。",
            target: () => tableCardRef.current,
        },
        {
            title: "号段入库",
            description: "最后，我们来录入一批主单号。点「打开并演示」，会进入弹窗逐字段带你填写。",
            target: () => createBtnRef.current,
            nextButtonProps: { children: "打开并演示" },
        },
    ];

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title">
                    <h2>主单号库存管理</h2>
                    <span className="page-description">管理航空公司主单号库存与分配</span>
                </div>
                <div className="page-actions">
                    <Space>
                        <Button icon={<QuestionCircleOutlined />} onClick={() => setTourOpen(true)}>
                            使用教程
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={() => refetch()} loading={isLoading}>
                            刷新
                        </Button>
                        <PermissionGuard permission="mawb:create">
                            <Button icon={<ImportOutlined />} onClick={() => setImportModalOpen(true)}>
                                导入单号
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="mawb:create">
                            <Button ref={createBtnRef} type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
                                新增入库
                            </Button>
                        </PermissionGuard>
                    </Space>
                </div>
            </div>

            <div ref={filterRef}>
                <Card className="filter-card" size="small">
                    <MawbStockFilter onSearch={handleSearch} onReset={handleReset} onRefresh={() => refetch()} />
                </Card>
            </div>

            <div ref={tableCardRef}>
                <Card className="data-card">
                    <div ref={tableWrapRef}>
                        <div style={{ marginBottom: 8, color: "#666", fontSize: 13 }}>
                            {stale ? "刷新没有成功，表格里还是上一次的结果" : `已加载 ${items.length} / 共 ${total} 条`}
                        </div>
                        <Table
                            size="small"
                            ref={tableRef}
                            columns={columns}
                            dataSource={items}
                            loading={isLoading}
                            rowKey="id"
                            pagination={false}
                            scroll={{ x: 1420, y: tableScrollY }}
                            footer={() =>
                                stale ? (
                                    <div style={{ textAlign: "center", color: "#999" }}>刷新没有成功，表格里还是上一次的结果</div>
                                ) : total > 0 ? (
                                    <div style={{ textAlign: "center" }}>
                                        {loadingMore ? (
                                            <span>正在加载…</span>
                                        ) : hasMore ? (
                                            <Button type="link" onClick={loadMore}>
                                                向下滚动或点击加载更多
                                            </Button>
                                        ) : (
                                            <span style={{ color: "#999" }}>已加载全部 {total} 条</span>
                                        )}
                                    </div>
                                ) : null
                            }
                        />
                    </div>
                </Card>
            </div>

            <MawbStockCreateModal
                open={createModalOpen}
                autoGuide={createAutoGuide}
                onCancel={() => { setCreateModalOpen(false); setCreateAutoGuide(false); }}
                onSuccess={() => { setCreateModalOpen(false); setCreateAutoGuide(false); refetch(); }}
            />

            <MawbImportModal
                open={importModalOpen}
                onCancel={() => setImportModalOpen(false)}
                onSuccess={() => { setImportModalOpen(false); refetch(); }}
            />

            <MawbAllocationModal
                open={allocModalOpen}
                onCancel={() => { setAllocModalOpen(false); setAllocStock(null); }}
                onSuccess={() => { setAllocModalOpen(false); setAllocStock(null); refetch(); }}
                stock={allocStock}
            />

            <Tour
                open={tourOpen}
                steps={tourSteps}
                onClose={() => setTourOpen(false)}
                onFinish={() => {
                    setTourOpen(false);
                    setCreateModalOpen(true);
                    setCreateAutoGuide(true);
                }}
            />
        </div>
    );
};

export default MawbStockListPage;
